import routes from 'js/core_pre_webpack/routes';
import { currentUser } from 'js/utils/user';

export const MEGA_MENU_TRIGGER = 'apps-mega-menu';
export const SUPPORT_LINK = 'https://support.academia.edu';

const ROUTE_OPTS = { from_navbar: true, trigger: MEGA_MENU_TRIGGER };

export type MegaMenuItem = {
  label: string;
  href: string;
  locked: boolean;
};

export type MegaMenuColumn = {
  heading: string;
  items: MegaMenuItem[];
};

export type MegaMenuUtilityIcon = 'premium-lock' | 'tune' | 'help' | 'article';

export type MegaMenuUtilityItem = {
  label: string;
  href: string;
  icon: MegaMenuUtilityIcon;
};

function networkTabUrl(tab: 'followers' | 'following'): string {
  return routes.network_new_profile_url({
    page_name: currentUser?.page_name,
    subdomain: currentUser?.domain_name,
    tab,
    ...ROUTE_OPTS,
  });
}

export function megaMenuColumns(isPremium: boolean): MegaMenuColumn[] {
  return [
    {
      heading: 'Find research',
      items: [
        {
          label: 'Reference finder',
          href: routes.citeright_index_url(ROUTE_OPTS),
          locked: false,
        },
        {
          label: 'Research topics',
          href: routes.research_interest_directory_url(ROUTE_OPTS),
          locked: false,
        },
        {
          label: 'Advanced search',
          href: isPremium
            ? routes.advanced_search_url(ROUTE_OPTS)
            : routes.upgrade_url({ feature: 'search', ...ROUTE_OPTS }),
          locked: true,
        },
        {
          label: 'Search alerts',
          href: isPremium
            ? routes.saved_searches_url(ROUTE_OPTS)
            : routes.upgrade_url({ feature: 'SAVED_SEARCH', ...ROUTE_OPTS }),
          locked: true,
        },
        {
          label: 'PDF Packs',
          href: routes.research_index_url(ROUTE_OPTS),
          locked: true,
        },
      ],
    },
    {
      heading: 'Track my impact',
      items: [
        {
          label: 'Paper views',
          href: `/analytics?from_navbar=true&trigger=${MEGA_MENU_TRIGGER}`,
          locked: false,
        },
        {
          label: 'Citations',
          href: routes.citations_url(ROUTE_OPTS),
          locked: true,
        },
        {
          label: 'Profile visitors',
          href: isPremium
            ? routes.analytics_dashboard_url({
                page_name: currentUser?.page_name,
                subdomain: currentUser?.domain_name,
              }) +
              `/activity/profile-visitors?from_navbar=true&trigger=${MEGA_MENU_TRIGGER}`
            : routes.upgrade_url({
                feature: 'profile_visitors',
                ...ROUTE_OPTS,
              }),
          locked: true,
        },
      ],
    },
    {
      heading: 'Publish',
      items: [
        {
          label: 'Academia.edu Journals',
          href: routes.academia_journals_url(ROUTE_OPTS),
          locked: false,
        },
        {
          label: 'All journal articles',
          href: routes.academia_journals_articles_url(ROUTE_OPTS),
          locked: false,
        },
        {
          label: 'My submissions',
          href: routes.journals_submissions_url(ROUTE_OPTS),
          locked: false,
        },
        {
          label: 'Reviewer Hub',
          href: routes.journals_submissions_url({
            anchor: 'reviewer',
            ...ROUTE_OPTS,
          }),
          locked: false,
        },
        {
          label: 'Editor Hub',
          href: routes.journals_submissions_url({
            anchor: 'editor',
            ...ROUTE_OPTS,
          }),
          locked: false,
        },
      ],
    },
    {
      heading: 'Network',
      items: [
        {
          label: 'Share research',
          href: routes.upload_funnel_url(ROUTE_OPTS),
          locked: false,
        },
        { label: 'Followers', href: networkTabUrl('followers'), locked: false },
        { label: 'Following', href: networkTabUrl('following'), locked: false },
        {
          label: 'Find colleagues',
          href: routes.friend_finder_index_url(ROUTE_OPTS),
          locked: false,
        },
        {
          label: 'Start a discussion',
          href: routes.s_url(ROUTE_OPTS),
          locked: false,
        },
      ],
    },
  ];
}

export function megaMenuUtilityItems(
  isPremium: boolean,
): MegaMenuUtilityItem[] {
  const upgradeItem: MegaMenuUtilityItem = {
    label: 'Try Academia Premium',
    href: routes.upgrade_url({ feature: 'default', ...ROUTE_OPTS }),
    icon: 'premium-lock',
  };

  return [
    ...(isPremium ? [] : [upgradeItem]),
    {
      label: 'Improve recommendations',
      href: routes.recommendations_url(ROUTE_OPTS),
      icon: 'tune',
    },
    { label: 'Help center', href: SUPPORT_LINK, icon: 'help' },
    {
      label: 'Community standards',
      href: routes.community_standards_url(ROUTE_OPTS),
      icon: 'article',
    },
  ];
}
